// ExecMembersTable.js
import React, { Component } from 'react';
import firebase from '../../store/firebase';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

class ExecMembersTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      allUsers: [],
      filteredUsers: [],
      loading: true,
      error: null,
      execFilter: 'exec',
      pledgeFilter: 'nopledge',
      companySearch: '',
      fundingFilter: 'all',           // new: 'all' | 'funded' | 'notfunded'
    };
  }

  componentDidMount() {
    const usersRef = firebase.database.ref('user');

    usersRef.on('value', (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const users = Object.keys(data)
          .map(key => ({
            id: key,
            ...data[key]
          }))
          .sort((a, b) => 
            (a.lastName || '').localeCompare(b.lastName || '')
          );

        this.setState({
          allUsers: users,
          loading: false
        }, () => this.applyFilters());
      } else {
        this.setState({
          allUsers: [],
          filteredUsers: [],
          loading: false
        });
      }
    }, (error) => {
      console.error("Firebase error:", error);
      this.setState({
        error: error.message,
        loading: false
      });
    });
  }

  componentWillUnmount() {
    firebase.database.ref('user').off('value');
  }

  handleExecFilterChange = (e) => {
    this.setState({ execFilter: e.target.value }, this.applyFilters);
  };

  handlePledgeFilterChange = (e) => {
    this.setState({ pledgeFilter: e.target.value }, this.applyFilters);
  };

  handleCompanySearchChange = (e) => {
    this.setState({ companySearch: e.target.value }, this.applyFilters);
  };

  handleFundingFilterChange = (e) => {
    this.setState({ fundingFilter: e.target.value }, this.applyFilters);
  };

  applyFilters = () => {
    const { allUsers, execFilter, pledgeFilter, companySearch, fundingFilter } = this.state;

    const searchLower = companySearch.trim().toLowerCase();

    const filtered = allUsers.filter(user => {
      // Exec filter
      const isExec = user.execmember === true;
      if (execFilter === 'exec' && !isExec) return false;
      if (execFilter === 'nonexec' && isExec) return false;

      // Pledge filter
      const pledge = user.pledge;
      const hasPledge = typeof pledge === 'number' && pledge > 0;
      if (pledgeFilter === 'nopledge' && hasPledge) return false;
      if (pledgeFilter === 'pledged' && !hasPledge) return false;

      // New: Initial Funding filter
      const hasFunding = user.hasReceivedInitialFunding === true;
      if (fundingFilter === 'funded' && !hasFunding) return false;
      if (fundingFilter === 'notfunded' && hasFunding) return false;
      // 'all' → no restriction

      // Company search
      if (searchLower) {
        const company = (user.companyName || '').toLowerCase();
        if (!company.includes(searchLower)) return false;
      }

      return true;
    });

    this.setState({ filteredUsers: filtered });
  };

  formatPledge = (amount) => {
    if (typeof amount !== 'number' || amount <= 0) return '-';
    return `$${(amount / 100).toFixed(2)}`;
  };

  // === EXCEL EXPORT FUNCTION ===
  downloadExcel = async () => {
    const { filteredUsers } = this.state;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Users');

    // Define columns (added Initial Funding)
    worksheet.columns = [
      { header: 'First Name', key: 'firstName', width: 15 },
      { header: 'Last Name', key: 'lastName', width: 15 },
      { header: 'Email', key: 'email', width: 30 },
      { header: 'Company', key: 'companyName', width: 25 },
      { header: 'Exec Member', key: 'execmember', width: 12 },
      { header: 'Pledge Amount', key: 'pledge', width: 15 },
      { header: 'Initial Funding', key: 'hasReceivedInitialFunding', width: 18 },
    ];

    // Add rows
    filteredUsers.forEach(user => {
      worksheet.addRow({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        companyName: user.companyName || '',
        execmember: user.execmember === true ? 'Yes' : 'No',
        pledge: this.formatPledge(user.pledge).replace('$', ''),
        hasReceivedInitialFunding: user.hasReceivedInitialFunding === true ? 'Yes' : 'No',
      });
    });

    // Style header
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD9EAD3' }
    };

    // Format pledge column as currency
    worksheet.getColumn('pledge').numFmt = '$#,##0.00';

    // Generate file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });
    saveAs(blob, 'User_Directory_Export.xlsx');
  };

  render() {
    const { filteredUsers, loading, error, execFilter, pledgeFilter, companySearch, fundingFilter } = this.state;

    if (loading) return <div>Loading users...</div>;
    if (error) return <div>Error loading data: {error}</div>;

    return (
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <h2>User Directory</h2>

        {/* Filter Controls + Download Button */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Exec Status:</label>
              <select value={execFilter} onChange={this.handleExecFilterChange} style={selectStyle}>
                <option value="exec">Active Executive Members</option>
                <option value="nonexec">Regular / Non-Exec Members</option>
              </select>
            </div>

            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Pledge Status:</label>
              <select value={pledgeFilter} onChange={this.handlePledgeFilterChange} style={selectStyle}>
                <option value="nopledge">Not Pledged</option>
                <option value="pledged">Has Pledged</option>
              </select>
            </div>

            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Initial Funding:</label>
              <select value={fundingFilter} onChange={this.handleFundingFilterChange} style={selectStyle}>
                <option value="all">All (Funded + Not Funded)</option>
                <option value="funded">Has Received Initial Funding</option>
                <option value="notfunded">No Initial Funding Yet</option>
              </select>
            </div>

            <div>
              <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Company:</label>
              <input
                type="text"
                value={companySearch}
                onChange={this.handleCompanySearchChange}
                placeholder="Search company name..."
                style={inputStyle}
              />
            </div>

            <div style={{ marginLeft: 'auto' }}>
              <button onClick={this.downloadExcel} style={buttonStyle}>
                📊 Download in Excel
              </button>
            </div>

            <div style={{ color: '#555', fontSize: '14px', width: '100%', marginTop: '8px' }}>
              Showing: <strong>{filteredUsers.length}</strong> user{filteredUsers.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredUsers.length === 0 ? (
          <p>No users match the selected filters.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f2f2f2' }}>
                <th style={thStyle}>First Name</th>
                <th style={thStyle}>Last Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Company</th>
                <th style={thStyle}>Exec?</th>
                <th style={thStyle}>Pledge</th>
                <th style={thStyle}>Initial Funding</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={tdStyle}>{user.firstName || '-'}</td>
                  <td style={tdStyle}>{user.lastName || '-'}</td>
                  <td style={tdStyle}>
                    <a href={`mailto:${user.email}`} style={{ color: '#0066cc' }}>
                      {user.email || '-'}
                    </a>
                  </td>
                  <td style={tdStyle}>{user.companyName || '-'}</td>
                  <td style={tdStyle}>{user.execmember === true ? 'Yes' : 'No'}</td>
                  <td style={tdStyle}>{this.formatPledge(user.pledge)}</td>
                  <td style={tdStyle}>
                    {user.hasReceivedInitialFunding === true ? 'Yes' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }
}

// Styles (unchanged)
const thStyle = { padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', backgroundColor: '#f9f9f9' };
const tdStyle = { padding: '12px', textAlign: 'left' };
const selectStyle = { padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' };
const inputStyle = { padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc', width: '200px' };
const buttonStyle = {
  padding: '10px 16px',
  fontSize: '14px',
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer'
};

export default ExecMembersTable;
//////////////////////////////////////////////////////////////////////////////////////////////
// // ExecMembersTable.js
// import React, { Component } from 'react';
// import firebase from '../../store/firebase';
// import ExcelJS from 'exceljs';
// import { saveAs } from 'file-saver';

// class ExecMembersTable extends Component {
//   constructor(props) {
//     super(props);
//     this.state = {
//       allUsers: [],
//       filteredUsers: [],
//       loading: true,
//       error: null,
//       execFilter: 'exec',
//       pledgeFilter: 'nopledge',
//       companySearch: ''
//     };
//   }

//   componentDidMount() {
//     const usersRef = firebase.database.ref('user');

//     usersRef.on('value', (snapshot) => {
//       const data = snapshot.val();
//       if (data) {
//         const users = Object.keys(data)
//           .map(key => ({
//             id: key,
//             ...data[key]
//           }))
//           .sort((a, b) => 
//             (a.lastName || '').localeCompare(b.lastName || '')
//           );

//         this.setState({
//           allUsers: users,
//           loading: false
//         }, () => this.applyFilters());
//       } else {
//         this.setState({
//           allUsers: [],
//           filteredUsers: [],
//           loading: false
//         });
//       }
//     }, (error) => {
//       console.error("Firebase error:", error);
//       this.setState({
//         error: error.message,
//         loading: false
//       });
//     });
//   }

//   componentWillUnmount() {
//     firebase.database.ref('user').off('value');
//   }

//   handleExecFilterChange = (e) => {
//     this.setState({ execFilter: e.target.value }, this.applyFilters);
//   };

//   handlePledgeFilterChange = (e) => {
//     this.setState({ pledgeFilter: e.target.value }, this.applyFilters);
//   };

//   handleCompanySearchChange = (e) => {
//     this.setState({ companySearch: e.target.value }, this.applyFilters);
//   };

//   applyFilters = () => {
//     const { allUsers, execFilter, pledgeFilter, companySearch } = this.state;

//     const searchLower = companySearch.trim().toLowerCase();

//     const filtered = allUsers.filter(user => {
//       const isExec = user.execmember === true;
//       if (execFilter === 'exec' && !isExec) return false;
//       if (execFilter === 'nonexec' && isExec) return false;

//       const pledge = user.pledge;
//       const hasPledge = typeof pledge === 'number' && pledge > 0;
//       if (pledgeFilter === 'nopledge' && hasPledge) return false;
//       if (pledgeFilter === 'pledged' && !hasPledge) return false;

//       if (searchLower) {
//         const company = (user.companyName || '').toLowerCase();
//         if (!company.includes(searchLower)) return false;
//       }

//       return true;
//     });

//     this.setState({ filteredUsers: filtered });
//   };

//   formatPledge = (amount) => {
//     if (typeof amount !== 'number' || amount <= 0) return '-';
//     return `$${(amount / 100).toFixed(2)}`;
//   };

//   // === EXCEL EXPORT FUNCTION ===
//   downloadExcel = async () => {
//     const { filteredUsers } = this.state;

//     const workbook = new ExcelJS.Workbook();
//     const worksheet = workbook.addWorksheet('Users');

//     // Define columns
//     worksheet.columns = [
//       { header: 'First Name', key: 'firstName', width: 15 },
//       { header: 'Last Name', key: 'lastName', width: 15 },
//       { header: 'Email', key: 'email', width: 30 },
//       { header: 'Company', key: 'companyName', width: 25 },
//       { header: 'Exec Member', key: 'execmember', width: 12 },
//       { header: 'Pledge Amount', key: 'pledge', width: 15 }
//     ];

//     // Add rows
//     filteredUsers.forEach(user => {
//       worksheet.addRow({
//         firstName: user.firstName || '',
//         lastName: user.lastName || '',
//         email: user.email || '',
//         companyName: user.companyName || '',
//         execmember: user.execmember === true ? 'Yes' : 'No',
//         pledge: this.formatPledge(user.pledge).replace('$', '') // Excel handles currency better without $
//       });
//     });

//     // Style header
//     worksheet.getRow(1).font = { bold: true };
//     worksheet.getRow(1).fill = {
//       type: 'pattern',
//       pattern: 'solid',
//       fgColor: { argb: 'FFD9EAD3' }
//     };

//     // Format pledge column as currency
//     worksheet.getColumn('pledge').numFmt = '$#,##0.00';

//     // Generate file
//     const buffer = await workbook.xlsx.writeBuffer();
//     const blob = new Blob([buffer], { type: 'application/octet-stream' });
//     saveAs(blob, 'User_Directory_Export.xlsx');
//   };

//   render() {
//     const { filteredUsers, loading, error, execFilter, pledgeFilter, companySearch } = this.state;

//     if (loading) return <div>Loading users...</div>;
//     if (error) return <div>Error loading data: {error}</div>;

//     return (
//       <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
//         <h2>User Directory</h2>

//         {/* Filter Controls + Download Button */}
//         <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
//           <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
//             <div>
//               <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Exec Status:</label>
//               <select value={execFilter} onChange={this.handleExecFilterChange} style={selectStyle}>
//                 <option value="exec">Active Executive Members</option>
//                 <option value="nonexec">Regular / Non-Exec Members</option>
//               </select>
//             </div>

//             <div>
//               <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Pledge Status:</label>
//               <select value={pledgeFilter} onChange={this.handlePledgeFilterChange} style={selectStyle}>
//                 <option value="nopledge">Not Pledged</option>
//                 <option value="pledged">Has Pledged</option>
//               </select>
//             </div>

//             <div>
//               <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Company:</label>
//               <input
//                 type="text"
//                 value={companySearch}
//                 onChange={this.handleCompanySearchChange}
//                 placeholder="Search company name..."
//                 style={inputStyle}
//               />
//             </div>

//             <div style={{ marginLeft: 'auto' }}>
//               <button onClick={this.downloadExcel} style={buttonStyle}>
//                 📊 Download in Excel
//               </button>
//             </div>

//             <div style={{ color: '#555', fontSize: '14px' }}>
//               Showing: <strong>{filteredUsers.length}</strong> user{filteredUsers.length !== 1 ? 's' : ''}
//             </div>
//           </div>
//         </div>

//         {/* Table */}
//         {filteredUsers.length === 0 ? (
//           <p>No users match the selected filters.</p>
//         ) : (
//           <table style={{ width: '100%', borderCollapse: 'collapse' }}>
//             <thead>
//               <tr style={{ backgroundColor: '#f2f2f2' }}>
//                 <th style={thStyle}>First Name</th>
//                 <th style={thStyle}>Last Name</th>
//                 <th style={thStyle}>Email</th>
//                 <th style={thStyle}>Company</th>
//                 <th style={thStyle}>Exec?</th>
//                 <th style={thStyle}>Pledge</th>
//               </tr>
//             </thead>
//             <tbody>
//               {filteredUsers.map(user => (
//                 <tr key={user.id} style={{ borderBottom: '1px solid #ddd' }}>
//                   <td style={tdStyle}>{user.firstName || '-'}</td>
//                   <td style={tdStyle}>{user.lastName || '-'}</td>
//                   <td style={tdStyle}>
//                     <a href={`mailto:${user.email}`} style={{ color: '#0066cc' }}>
//                       {user.email || '-'}
//                     </a>
//                   </td>
//                   <td style={tdStyle}>{user.companyName || '-'}</td>
//                   <td style={tdStyle}>{user.execmember === true ? 'Yes' : 'No'}</td>
//                   <td style={tdStyle}>{this.formatPledge(user.pledge)}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         )}
//       </div>
//     );
//   }
// }

// // Styles
// const thStyle = { padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', backgroundColor: '#f9f9f9' };
// const tdStyle = { padding: '12px', textAlign: 'left' };
// const selectStyle = { padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' };
// const inputStyle = { padding: '8px 12px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc', width: '200px' };
// const buttonStyle = {
//   padding: '10px 16px',
//   fontSize: '14px',
//   backgroundColor: '#4CAF50',
//   color: 'white',
//   border: 'none',
//   borderRadius: '4px',
//   cursor: 'pointer'
// };

// export default ExecMembersTable;
////////////////////////////////////////////////////////////////////////////////////////////////
